import React, { useEffect, useRef, useState } from "react";
import "./App.css";

const keywords = [
  "NLP",
  "Reinforcement Learning",
  "Computer Vision",
  "Multilingual AI",
  "Ethical AI",
  "Unreal Engine",
  "Game AI",
  "Game Development",
  "Transformers",
  "Personalisation",
  "CompSci Education"
];

const projects = [
  {
    title: "AI Dungeon Agent",
    description:
      "Reinforcement learning agent trained inside Unreal Engine for procedural storytelling.",
    tags: ["RL", "NLP", "Unreal Engine"],
  },
  {
    title: "Irish Language LLM",
    description:
      "A large language model fine-tuned for low-resource Irish language tasks.",
    tags: ["LLM", "Low-resource NLP", "Hugging Face"],
  },
  {
    title: "Hate Speech Dataset",
    description:
      "An open-source dataset for detecting LGBTQIA+ hate speech across multiple platforms.",
    tags: ["NLP", "Ethical AI", "Datasets"],
  },
];

// soft-but-vibrant palette (blue/yellow forward)
const palette = [
  "#1E88E5", // blue
  "#FFB300", // warm yellow
  "#26C6DA", // cyan
  "#7E57C2", // purple
  "#43A047", // green
  "#FB8C00", // orange
];

// deterministic color per word
const pickColor = (rowIndex, i) => palette[(rowIndex + i) % palette.length];

const getRandomKeywords = (count = 20) => {
  const randomWords = [];
  let prevWord = null;

  for (let i = 0; i < count; i++) {
    let nextWord;
    do {
      const randomIndex = Math.floor(Math.random() * keywords.length);
      nextWord = keywords[randomIndex];
    } while (nextWord === prevWord); // Avoid immediate repetition

    randomWords.push(nextWord);
    prevWord = nextWord;
  }

  return randomWords;
};


const staticText = "Josh McGiff is all about ";

export default function App() {
  const textRef = useRef(null);
  const keywordRef = useRef(null);
  const indexRef = useRef(0);
  const [typingDone, setTypingDone] = useState(false); // still okay to use once

  // Typewriter effect (DOM-only)
  useEffect(() => {
    const interval = setInterval(() => {
      if (indexRef.current < staticText.length) {
        textRef.current.textContent  += staticText[indexRef.current];
        indexRef.current += 1;
      } else {
        clearInterval(interval);
        setTypingDone(true);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Keyword flashing (DOM-only)
  useEffect(() => {
    if (!typingDone) return;
  
    let currentIndex = 0;
  
    // Show the first keyword immediately
    if (keywordRef.current) {
      keywordRef.current.innerText = keywords[currentIndex];
    }
  
    // Cycle through keywords in order
    const keywordInterval = setInterval(() => {
      currentIndex = (currentIndex + 1) % keywords.length;
      if (keywordRef.current) {
        keywordRef.current.innerText = keywords[currentIndex];
      }
    }, 1000);
  
    return () => clearInterval(keywordInterval);
  }, [typingDone]);
  
  const [rows] = useState(() =>
    [...Array(10)].map(() => getRandomKeywords(30))
  );

  return (
    <>
    <div className="splash">
      {/* Moving Keywords Background */}
      <div className="keywords-wall">
        {rows.map((randomRowWords, rowIndex) => (
          <div
            className={`keyword-row ${rowIndex % 2 === 0 ? "left" : "right"}`}
            key={rowIndex}
          >
            <div className="scroll-track">
              <div className="scroll-content">
                {randomRowWords.map((word, i) => (
                  <span key={`a-${rowIndex}-${i}`} style={{ color: pickColor(rowIndex, i) }}>
                    {word}&nbsp;
                  </span>
                ))}
              </div>
              <div className="scroll-content" aria-hidden="true">
                {randomRowWords.map((word, i) => (
                  <span key={`b-${rowIndex}-${i}`} style={{ color: pickColor(rowIndex, i) }}>
                    {word}&nbsp;
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>




      {/* Center text content */}
      <div className="splash-content">
        <h1 className="name">
          <span ref={textRef}></span>
          
        </h1>
        {/* {typingDone && ( */}
            <h1 className="keyword-box name"   style={{ opacity: typingDone ? 1 : 0 }} ref={keywordRef}>AI/ML</h1>
          {/* )} */}

        <h2 className="title">PhD Researcher in AI / NLP</h2>
      </div>
    </div>

    <section id="about" className="section">
      <h1 className="heading">Who is Josh McGiff?</h1>
      <div className="row-divider">
        <img style={{ boxShadow: "-29px 39px 0px #1e88e5b3"}} src="josh.png"></img>
        <p style={{textAlign: 'left'}}>
          Hello! I am a PhD researcher focused on building LLMs for low-resource languages such as the Irish language. I have also contributed to the field of LGBTQIA+ hate speech detection through the release of open-source datasets and models. 🌈
        </p>
      </div>

      <div className="row-divider">
        <p style={{textAlign: 'right'}}>
          Although I spend a lot of time diving into the technical details of all things AI/ML, I am a creative at heart. I truly love using my technical skills to build games and software experiences in my spare time. I am currently interested in personalising services (such as games) using AI. 
        </p>
        <img style={{ boxShadow: "37px -34px 0px #43a0479c"}} src="josh2.png"></img>

      </div>

      <div className="row-divider">
        <img style={{ boxShadow: "-29px 39px 0px #FFB3009c"}} src="josh3.png"></img>
        <p style={{textAlign: 'left'}}>
          NLP is my passion but I have also worked with reinforcement learning, computer vision and general software engineering. I have over five years of teaching experience at the University of Limerick, including a year of lecturing two of my own modules (Software Design & Architecture and IGNITE). I also have five years of experience building apps for the Streamlabs App Store.
        </p>
      </div>
  
    </section>

    <section id="projects" className="section">
      <h1 className="heading">Projects / Blog</h1>
      <p className="heading">Coming soon</p>
      {/* <div className="project-grid">
        {projects.map((proj, index) => (
          <div className="project-card" key={index}>
            <h3>{proj.title}</h3>
            <p>{proj.description}</p>
            <div className="tags">
              {proj.tags.map((tag, i) => (
                <span key={i} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div> */}
    </section>

    <section id="socials" className="section">
      <h1 className="heading">Find me</h1>
      <div className="social-links">
        <a
          href="https://www.linkedin.com/in/joshmcgiff/"
          target="_blank"
          rel="noopener noreferrer"
          className="social-btn linkedin"
        >
          LinkedIn
        </a>
        <a
          href="https://github.com/joshmcgiff"
          target="_blank"
          rel="noopener noreferrer"
          className="social-btn github"
        >
          GitHub
        </a>
        <a
          href="https://bsky.app/profile/joshtheoptimist.bsky.social"
          target="_blank"
          rel="noopener noreferrer"
          className="social-btn bluesky"
        >
          Bluesky
        </a>
      </div>
    </section>




    </>
  );
}
